import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../../components/feature/TopNavigation';

export default function Home() {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState<Set<number>>(new Set());
  const [hoveredRange, setHoveredRange] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-section') || '0');
            setVisibleSections(prev => new Set(prev).add(index));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-section]').forEach((el) => observer.observe(el));
    
    return () => observer.disconnect();
  }, []);

  const scoreRanges = [
    { 
      range: '900-1000', 
      label: 'Excellent', 
      color: 'bg-green-500',
      description: 'Outstanding health metrics across all categories',
      tips: ['Maintain current habits', 'Regular check-ups', 'Continue balanced lifestyle']
    },
    { 
      range: '700-899', 
      label: 'Good', 
      color: 'bg-teal-500',
      description: 'Strong health foundation with room for optimization',
      tips: ['Focus on weaker categories', 'Increase activity slightly', 'Maintain sleep schedule']
    },
    { 
      range: '500-699', 
      label: 'Fair', 
      color: 'bg-amber-500',
      description: 'Moderate health with several improvement opportunities',
      tips: ['Address lifestyle factors', 'Improve nutrition', 'Consult healthcare provider']
    },
    { 
      range: '300-499', 
      label: 'Needs Attention', 
      color: 'bg-orange-500',
      description: 'Significant areas requiring immediate attention',
      tips: ['Seek medical guidance', 'Start small changes', 'Track progress weekly']
    },
    { 
      range: '0-299', 
      label: 'Critical', 
      color: 'bg-red-500',
      description: 'Urgent health concerns requiring professional intervention',
      tips: ['Schedule doctor visit immediately', 'Get comprehensive evaluation', 'Follow medical advice']
    },
  ];

  const categories = [
    {
      icon: 'ri-heart-pulse-line',
      name: 'Cardiovascular',
      weight: '23%',
      description: 'Heart health, blood pressure, activity level',
      factors: ['Exercise frequency', 'Blood pressure', 'Smoking status', 'Cardio fitness']
    },
    {
      icon: 'ri-scales-3-line',
      name: 'Metabolic',
      weight: '27%',
      description: 'BMI, nutrition, blood sugar levels',
      factors: ['Healthy weight', 'Diet quality', 'Blood sugar', 'Nutrient intake']
    },
    {
      icon: 'ri-run-line',
      name: 'Lifestyle',
      weight: '25%',
      description: 'Daily habits, exercise, alcohol use',
      factors: ['Physical activity', 'Alcohol consumption', 'Daily movement', 'Exercise routine']
    },
    {
      icon: 'ri-hotel-bed-line',
      name: 'Recovery',
      weight: '15%',
      description: 'Sleep quality, duration, consistency',
      factors: ['Sleep duration', 'Sleep quality', 'Sleep schedule', 'Rest patterns']
    },
    {
      icon: 'ri-mental-health-line',
      name: 'Mental Wellbeing',
      weight: '10%',
      description: 'Stress levels, work-life balance',
      factors: ['Stress management', 'Work-life balance', 'Mental health', 'Social connections']
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <TopNavigation />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50">
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/5"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full mb-6 shadow-sm animate-fadeIn">
            <i className="ri-heart-pulse-line text-teal-600"></i>
            <span className="text-sm font-semibold text-gray-700">Evidence-Based Health Scoring</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
            Know Your<br />
            <span className="text-teal-600">Health Score</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-700 mb-10 max-w-3xl mx-auto animate-fadeIn" style={{ animationDelay: '0.2s' }}>
            Get a comprehensive, data-driven assessment of your overall health. 
            Understand your current state and discover personalized ways to improve.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fadeIn" style={{ animationDelay: '0.3s' }}>
            <button
              onClick={() => navigate('/onboarding')}
              className="px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-all hover:shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer text-lg"
            >
              <i className="ri-heart-add-line mr-2"></i>
              Get My Health Score
            </button>
            <button
              onClick={() => navigate('/score-explained')}
              className="px-8 py-4 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-teal-600 hover:text-teal-600 transition-all whitespace-nowrap cursor-pointer text-lg"
            >
              <i className="ri-question-line mr-2"></i>
              How It Works
            </button>
          </div>

          {/* Preview Score Display */}
          <div className="mt-16 inline-block bg-white rounded-2xl shadow-2xl p-8 animate-fadeIn" style={{ animationDelay: '0.4s' }}>
            <div className="flex items-center gap-8">
              <div className="relative">
                <div className="text-6xl font-bold text-teal-600">782</div>
                <div className="text-sm text-gray-600">out of 1000</div>
              </div>
              <div className="text-left">
                <div className="text-sm text-gray-600 mb-1">Example Score</div>
                <div className="text-2xl font-bold text-gray-900">Good Health</div>
                <div className="text-sm text-gray-600 mt-2">Based on 5 key categories</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-3xl text-teal-600"></i>
        </div>
      </section>

      {/* Score Ranges Section */}
      <section 
        data-section="0"
        className={`py-20 px-6 bg-white transition-all duration-1000 ${visibleSections.has(0) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Understanding Your Score Range
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hover over each range to see detailed definitions and improvement tips
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {scoreRanges.map((range, index) => (
              <div
                key={index}
                className="relative bg-white border-2 border-gray-200 rounded-xl p-6 transition-all duration-300 hover:border-teal-300 hover:shadow-xl hover:-translate-y-2 cursor-pointer"
                onMouseEnter={() => setHoveredRange(range.range)}
                onMouseLeave={() => setHoveredRange(null)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-4 h-4 rounded-full ${range.color} ${hoveredRange === range.range ? 'animate-pulse' : ''}`}></div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{range.range}</div>
                    <div className="text-sm text-gray-600">{range.label}</div>
                  </div>
                </div>
                
                <p className="text-sm text-gray-700 mb-4">{range.description}</p>

                {hoveredRange === range.range && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg animate-slideDown">
                    <div className="text-xs font-semibold text-gray-900 mb-2">💡 Impact Tips:</div>
                    <ul className="space-y-1">
                      {range.tips.map((tip, i) => (
                        <li key={i} className="text-xs text-gray-700 flex items-start gap-2">
                          <span className="text-teal-600 mt-0.5">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section 
        data-section="1"
        className={`py-20 px-6 bg-gradient-to-br from-gray-50 to-teal-50 transition-all duration-1000 ${visibleSections.has(1) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              5 Key Health Categories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Each category is weighted based on health research to provide your overall score
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 transition-all duration-300 hover:border-teal-300 hover:shadow-xl hover:-translate-y-2"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className={`${category.icon} text-3xl text-teal-600`}></i>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-teal-600">{category.weight}</div>
                    <div className="text-xs text-gray-500">Impact</div>
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-2">{category.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{category.description}</p>
                
                <div className="border-t-2 border-gray-100 pt-4">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Key Factors:</div>
                  <div className="space-y-1">
                    {category.factors.map((factor, i) => (
                      <div key={i} className="text-xs text-gray-600 flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-teal-600"></i>
                        {factor}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals - NEW SECTION */}
      <section className="py-16 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {/* 100% Private */}
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-2xl mx-auto mb-4">
                  <i className="ri-shield-check-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">100% Private</h3>
                <p className="text-gray-600 leading-relaxed">
                  Your data stays yours. Always.
                </p>
              </div>

              {/* Secure by Design */}
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-2xl mx-auto mb-4">
                  <i className="ri-lock-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Secure by Design</h3>
                <p className="text-gray-600 leading-relaxed">
                  Encrypted, protected, and never sold.
                </p>
              </div>

              {/* Fully Transparent */}
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-2xl mx-auto mb-4">
                  <i className="ri-eye-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Fully Transparent</h3>
                <p className="text-gray-600 leading-relaxed">
                  No black boxes. Every point explained.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Preview Section - STRATEGIC ADDITION */}
      <section className="py-20 bg-white border-y border-gray-200">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">See What You'll Get</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A complete breakdown of your health with actionable insights
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            {/* Mock Dashboard Preview */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl border-2 border-gray-200 p-8 shadow-lg">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Left: Score Meter */}
                <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-6">
                  <div className="flex flex-col items-center">
                    <div className="relative">
                      <svg className="w-48 h-48 transform -rotate-90">
                        <circle
                          cx="96"
                          cy="96"
                          r="88"
                          stroke="#e5e7eb"
                          strokeWidth="12"
                          fill="none"
                        />
                        <circle
                          cx="96"
                          cy="96"
                          r="88"
                          stroke="#14b8a6"
                          strokeWidth="12"
                          fill="none"
                          strokeDasharray={553}
                          strokeDashoffset={121}
                          strokeLinecap="round"
                          className="transition-all duration-1000"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="text-5xl font-bold text-gray-900">782</div>
                        <div className="text-xs text-gray-600 mt-1">out of 1000</div>
                      </div>
                    </div>
                    <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="font-semibold text-gray-900">Good</span>
                    </div>
                  </div>
                </div>

                {/* Right: Category Breakdown */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Category Breakdown</h3>
                  
                  {[
                    { name: 'Physical Activity', score: 85, icon: 'ri-run-line', color: 'bg-teal-500' },
                    { name: 'Sleep Quality', score: 72, icon: 'ri-moon-line', color: 'bg-purple-500' },
                    { name: 'Nutrition', score: 68, icon: 'ri-restaurant-line', color: 'bg-orange-500' },
                    { name: 'Mental Health', score: 80, icon: 'ri-心-line', color: 'bg-pink-500' }
                  ].map((category) => (
                    <div key={category.name} className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 flex items-center justify-center ${category.color} rounded-lg`}>
                            <i className={`${category.icon} text-white`}></i>
                          </div>
                          <span className="font-semibold text-gray-900 text-sm">{category.name}</span>
                        </div>
                        <span className="text-lg font-bold text-gray-900">{category.score}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`${category.color} h-2 rounded-full transition-all duration-500`}
                          style={{ width: `${category.score}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Example Improvement Callout */}
              <div className="mt-6 bg-green-50 border-2 border-green-200 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                    <i className="ri-arrow-up-line text-xl text-green-600"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">Recent Improvement</h4>
                    <p className="text-sm text-gray-700">
                      <span className="font-bold text-green-600">+12 points</span> from increased physical activity this week
                    </p>
                  </div>
                </div>
              </div>

              {/* Personalized Recommendation Preview */}
              <div className="mt-4 bg-blue-50 border-2 border-blue-200 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                    <i className="ri-lightbulb-line text-xl text-blue-600"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">Top Recommendation</h4>
                    <p className="text-sm text-gray-700">
                      Adding 30 minutes of moderate exercise 3x/week could boost your score by <span className="font-bold text-blue-600">+25 points</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Indicator */}
            <div className="text-center mt-8">
              <p className="text-sm text-gray-600 mb-4">This is what your personalized dashboard will look like</p>
              <button
                onClick={() => navigate('/onboarding')}
                className="px-8 py-4 bg-teal-600 text-white text-lg font-bold rounded-xl hover:bg-teal-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Get My Personal Score Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Score Ranges Preview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Understanding Your Score</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Just like credit scores, your Health Score falls into clear ranges that help you understand where you stand
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {[
              { range: '900-1000', label: 'Excellent', color: 'bg-emerald-500', desc: 'Outstanding health profile' },
              { range: '800-899', label: 'Very Good', color: 'bg-teal-500', desc: 'Strong health indicators' },
              { range: '700-799', label: 'Good', color: 'bg-blue-500', desc: 'Solid health foundation' },
              { range: '600-699', label: 'Fair', color: 'bg-amber-500', desc: 'Room for improvement' },
              { range: '0-599', label: 'Needs Attention', color: 'bg-red-500', desc: 'Multiple areas with high potential for improvement' }
            ].map((item) => (
              <div key={item.range} className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-6">
                  <div className={`w-20 h-20 ${item.color} rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0`}>
                    {item.range.split('-')[0]}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">{item.label}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why HealthScore?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A transparent, science-based approach to understanding your health
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-xl mb-6">
                <i className="ri-eye-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Fully Transparent</h3>
              <p className="text-gray-600 leading-relaxed">
                See exactly how your score is calculated. Every factor is explained in plain language, just like FICO credit scores.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center bg-purple-100 rounded-xl mb-6">
                <i className="ri-line-chart-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Track Progress</h3>
              <p className="text-gray-600 leading-relaxed">
                Watch your score improve over time as you make positive changes. Get personalized recommendations for maximum impact.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center bg-pink-100 rounded-xl mb-6">
                <i className="ri-shield-check-line text-3xl text-pink-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Privacy First</h3>
              <p className="text-gray-600 leading-relaxed">
                Your health data is encrypted and never shared with insurers, employers, or third parties. You're in complete control.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple 3-Step Process</h2>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white text-2xl font-bold rounded-full mx-auto mb-6">
                  1
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Quick Onboarding</h3>
                <p className="text-gray-600">
                  Answer basic questions about your health, lifestyle, and habits. Takes less than 5 minutes.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white text-2xl font-bold rounded-full mx-auto mb-6">
                  2
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Get Your Score</h3>
                <p className="text-gray-600">
                  Receive your Health Score with detailed breakdown of all contributing factors.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white text-2xl font-bold rounded-full mx-auto mb-6">
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Improve Over Time</h3>
                <p className="text-gray-600">
                  Follow personalized recommendations and watch your score improve as you build healthier habits.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-emerald-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Know Your Health Score?
          </h2>
          <p className="text-xl text-teal-50 mb-10 max-w-2xl mx-auto">
            Join thousands taking control of their health with transparent, actionable insights
          </p>
          <button
            onClick={() => navigate('/onboarding')}
            className="px-10 py-4 bg-white text-teal-600 text-lg font-bold rounded-xl hover:bg-gray-50 transition-colors shadow-xl whitespace-nowrap cursor-pointer"
          >
            Get Started Free
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-600 rounded-lg">
                <i className="ri-heart-pulse-line text-2xl text-white"></i>
              </div>
              <span className="text-xl font-bold">HealthScore</span>
            </div>
            <div className="text-sm text-gray-400 text-center">
              HealthScore provides informational insights only and is not intended to diagnose, treat, or replace professional medical advice.
            </div>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
